/**
 * Centralized Hospital Lab-Set registry (single source of truth).
 *
 * A LAB SET is a parent panel (e.g. "CBC", "Renal function test"); a LAB ITEM is a
 * child test inside it (e.g. Hb, Creatinine). The whole app maps a panel name /
 * abbreviation / alias / common typo to its child items so the user adds the panel
 * ONCE and gets all children — instead of adding each child by hand.
 *
 * Design (per the lab-list spec):
 *  - Built-in sets live HERE (static config), not scattered across the codebase.
 *  - The web Project Builder lab picker and the search/autocomplete both consume this.
 *  - Composite sets (e.g. "Sepsis workup" contains CBC + Electrolytes) reference other
 *    sets by id and are expanded RECURSIVELY + de-duplicated by `expandLabSet`.
 *  - Clinical content validated by the uni-doctor agent (2026-06-10): "Cardiac enzymes"
 *    excludes BNP/NT-proBNP (those are heart-failure markers); VBG SvO2 is optional.
 */
/** Concise child-item constructor: I('Hemoglobin','Hb') or I('RBC'). */
const I = (name, code) => ({ name, code: code || name });
/** Reference to another set (expanded recursively by expandLabSet). */
const REF = (id, label) => ({ code: id, name: label });
export const LAB_SETS = [
    // ---------------- Hematology ----------------
    {
        id: 'cbc', name: 'CBC (Complete blood count)', category: 'Hematology', price: 150,
        aliases: ['cbc', 'complete blood count', 'complete blood count with differential', 'cbc diff', 'cbc with diff', 'fbc', 'full blood count', 'full blood examination'],
        items: [I('RBC'), I('Hemoglobin', 'Hb'), I('Hematocrit', 'Hct'), I('WBC'), I('Platelet'), I('Neutrophil'), I('Lymphocyte'), I('Monocyte'), I('Eosinophil'), I('Basophil')],
    },
    {
        id: 'cbc-basic', name: 'CBC basic', category: 'Hematology', price: 120,
        aliases: ['cbc basic', 'complete blood count without differential', 'cbc no diff', 'cbc only'],
        items: [I('RBC'), I('Hemoglobin', 'Hb'), I('Hematocrit', 'Hct'), I('WBC'), I('Platelet')],
    },
    {
        id: 'wbc-diff', name: 'WBC differential', category: 'Hematology', price: 90,
        aliases: ['wbc diff', 'wbc differential', 'differential count', 'white blood cell differential', 'diff count', 'dlc'],
        items: [I('Neutrophil'), I('Lymphocyte'), I('Monocyte'), I('Eosinophil'), I('Basophil')],
    },
    // ---------------- Renal / Chemistry ----------------
    {
        id: 'rft', name: 'Renal function test (BUN/Cr/eGFR)', category: 'Renal function', price: 180,
        aliases: ['renal function test', 'rft', 'kidney function test', 'renal panel', 'urea creatinine', 'bun cr', 'bun creatinine', 'uec', 'u&e', 'euc'],
        items: [I('BUN'), I('Creatinine', 'Cr'), I('eGFR')],
    },
    {
        id: 'electrolytes', name: 'Electrolytes (Na/K/Cl/CO₂)', category: 'Electrolytes', price: 150,
        aliases: ['electrolytes', 'elyte', 'e lyte', 'serum electrolytes', 'electrolyte panel', 'na k cl co3', 'na k cl hco3', 'u and e'],
        items: [I('Sodium', 'Na'), I('Potassium', 'K'), I('Chloride', 'Cl'), I('Bicarbonate (CO₂/HCO₃)', 'CO2')],
    },
    {
        id: 'bmp', name: 'Basic metabolic panel (BMP)', category: 'Chemistry', price: 320,
        aliases: ['bmp', 'basic metabolic panel', 'chem 7', 'chem-7', 'chem 8', 'chem-8', 'basic chemistry', 'basic chem panel'],
        items: [I('Sodium', 'Na'), I('Potassium', 'K'), I('Chloride', 'Cl'), I('Bicarbonate (CO₂/HCO₃)', 'CO2'), I('BUN'), I('Creatinine', 'Cr'), I('Glucose'), I('Calcium', 'Ca')],
    },
    {
        id: 'cmp', name: 'Comprehensive metabolic panel (CMP)', category: 'Chemistry', price: 480,
        aliases: ['cmp', 'comprehensive metabolic panel', 'chem 12', 'chem-12', 'chem 14', 'chem-14', 'full chemistry panel', 'complete chemistry panel'],
        items: [I('Sodium', 'Na'), I('Potassium', 'K'), I('Chloride', 'Cl'), I('Bicarbonate (CO₂/HCO₃)', 'CO2'), I('BUN'), I('Creatinine', 'Cr'), I('Glucose'), I('Calcium', 'Ca'), I('Total protein'), I('Albumin'), I('Total bilirubin'), I('ALP'), I('AST'), I('ALT')],
    },
    // ---------------- Liver ----------------
    {
        id: 'lft', name: 'Liver function test (LFT)', category: 'Liver function', price: 280,
        aliases: ['lft', 'lfts', 'liver function test', 'liver panel', 'hepatic panel', 'liver enzymes', 'hepatic function panel'],
        items: [I('AST (SGOT)', 'AST'), I('ALT (SGPT)', 'ALT'), I('ALP'), I('Total bilirubin'), I('Direct bilirubin'), I('Indirect bilirubin'), I('Albumin'), I('Globulin'), I('Total protein'), I('GGT')],
    },
    // ---------------- Coagulation ----------------
    {
        id: 'coag', name: 'Coagulation profile', category: 'Coagulation', price: 250,
        aliases: ['coagulation profile', 'coag profile', 'coags', 'clotting profile', 'bleeding profile', 'pt ptt inr', 'pt aptt inr'],
        items: [I('PT'), I('INR'), I('aPTT')],
    },
    // ---------------- Endocrine ----------------
    {
        id: 'tft', name: 'Thyroid function test (TSH/FT4/FT3)', category: 'Endocrine', price: 600,
        aliases: ['tft', 'tfts', 'thyroid function test', 'thyroid function tests', 'thyroid panel', 'thyroid profile', 'ft3 ft4 tsh', 'thyroid'],
        items: [I('TSH'), I('Free T4', 'FT4'), I('Free T3', 'FT3')],
    },
    // ---------------- Lipids / Diabetes ----------------
    {
        id: 'lipid', name: 'Lipid profile', category: 'Lipids', price: 220,
        aliases: ['lipid profile', 'lipid panel', 'cholesterol panel', 'lipids', 'flp', 'fasting lipid profile'],
        items: [I('Total cholesterol'), I('Triglyceride'), I('HDL-C'), I('LDL-C')],
    },
    {
        id: 'dm-profile', name: 'Diabetes profile', category: 'Endocrine / Diabetes', price: 280,
        aliases: ['diabetes profile', 'dm profile', 'sugar profile', 'glucose profile', 'blood sugar profile'],
        items: [I('FBS'), I('HbA1c')],
    },
    {
        id: 'glucose', name: 'Blood glucose', category: 'Chemistry / Diabetes', price: 60,
        aliases: ['blood sugar', 'glucose', 'bs', 'dtx', 'cbg', 'fbs', 'rbs', 'blood glucose'],
        items: [I('Glucose')],
    },
    // ---------------- Bone / minerals ----------------
    {
        id: 'ca-mg-po4', name: 'Calcium magnesium phosphate', category: 'Electrolytes', price: 270,
        aliases: ['ca mg po4', 'ca mg phos', 'bone mineral panel', 'mineral panel', 'calcium magnesium phosphate'],
        items: [I('Calcium', 'Ca'), I('Magnesium', 'Mg'), I('Phosphate', 'PO4')],
    },
    {
        id: 'bone-profile', name: 'Bone profile', category: 'Chemistry / Bone', price: 320,
        aliases: ['bone profile', 'bone panel', 'bone chemistry', 'calcium phosphate alp', 'bone mineral profile'],
        items: [I('Calcium', 'Ca'), I('Phosphate', 'PO4'), I('ALP'), I('Albumin')],
    },
    // ---------------- Cardiac ----------------
    {
        id: 'cardiac-enzymes', name: 'Cardiac enzymes', category: 'Cardiac markers', price: 700,
        aliases: ['cardiac enzymes', 'cardiac markers', 'mi markers', 'acs labs', 'troponin ckmb', 'trop ckmb'],
        // Validated: BNP/NT-proBNP are heart-failure markers, NOT cardiac enzymes — excluded.
        items: [I('Troponin I/T', 'Troponin'), I('CK-MB'), I('CK')],
    },
    // ---------------- Inflammation / Infection ----------------
    {
        id: 'inflammatory', name: 'Inflammatory markers', category: 'Inflammatory markers', price: 250,
        aliases: ['inflammatory markers', 'inflammation labs', 'sepsis markers', 'infection markers', 'crp esr', 'crp procal', 'crp pct'],
        items: [I('CRP'), I('ESR')],
    },
    {
        id: 'sepsis', name: 'Sepsis workup', category: 'Infection / Emergency', price: 1200,
        aliases: ['sepsis workup', 'septic workup', 'fever workup', 'infection workup', 'septic labs'],
        items: [REF('cbc', 'CBC'), I('BUN'), I('Creatinine', 'Cr'), I('Sodium', 'Na'), I('Potassium', 'K'), I('Chloride', 'Cl'), I('Bicarbonate (CO₂/HCO₃)', 'CO2'), I('Lactate'), I('CRP'), I('Blood culture')],
    },
    // ---------------- Blood gas ----------------
    {
        id: 'abg', name: 'Arterial blood gas (ABG)', category: 'Blood gas', price: 350,
        aliases: ['abg', 'arterial blood gas', 'blood gas arterial', 'abg with lactate'],
        items: [I('pH'), I('PaCO2'), I('PaO2'), I('HCO3'), I('Base excess'), I('SaO2')],
    },
    {
        id: 'vbg', name: 'Venous blood gas (VBG)', category: 'Blood gas', price: 300,
        aliases: ['vbg', 'venous blood gas', 'blood gas venous', 'vbg with lactate'],
        // Validated: SvO2 needs a central/PA line — kept out of the core VBG set.
        items: [I('pH'), I('PvCO2'), I('HCO3'), I('Base excess')],
    },
    // ---------------- Urine ----------------
    {
        id: 'ua', name: 'Urinalysis (UA)', category: 'Urine', price: 110,
        aliases: ['ua', 'u/a', 'urine analysis', 'urinalysis', 'urine routine', 'urine examination', 'urine r/e', 'urine dipstick', 'urine microscopy'],
        items: [I('Color'), I('Specific gravity'), I('pH'), I('Protein'), I('Glucose'), I('Ketone'), I('Blood'), I('Nitrite'), I('Leukocyte esterase'), I('WBC (urine)', 'WBC'), I('RBC (urine)', 'RBC'), I('Epithelial cell'), I('Cast'), I('Crystal'), I('Bacteria')],
    },
    {
        id: 'urine-protein', name: 'Urine protein assessment', category: 'Urine / Renal', price: 200,
        aliases: ['urine protein', 'proteinuria workup', 'upcr', 'uacr', 'urine albumin creatinine ratio', 'urine protein creatinine ratio'],
        items: [I('Urine protein'), I('Urine creatinine'), I('UPCR')],
    },
    // ---------------- Stool / CSF ----------------
    {
        id: 'stool-exam', name: 'Stool examination', category: 'Stool', price: 120,
        aliases: ['stool exam', 'stool examination', 'stool routine', 'stool r/e', 'stool ova parasite', 'stool o&p', 'stool occult blood'],
        items: [I('Stool WBC'), I('Stool RBC'), I('Ova and parasite'), I('Occult blood')],
    },
    {
        id: 'csf', name: 'CSF analysis', category: 'CSF', price: 600,
        aliases: ['csf', 'csf study', 'csf analysis', 'lumbar puncture labs', 'lp labs', 'csf profile'],
        items: [I('CSF opening pressure'), I('CSF cell count'), I('CSF differential'), I('CSF protein'), I('CSF glucose'), I('CSF Gram stain'), I('CSF culture')],
    },
    // ---------------- Iron / Anemia ----------------
    {
        id: 'iron-studies', name: 'Iron studies', category: 'Hematology / Iron', price: 450,
        aliases: ['iron studies', 'iron profile', 'iron panel', 'anemia iron workup', 'fe tibc ferritin'],
        items: [I('Serum iron'), I('Ferritin'), I('TIBC'), I('Transferrin saturation')],
    },
    {
        id: 'anemia-workup', name: 'Anemia workup', category: 'Hematology', price: 900,
        aliases: ['anemia workup', 'anaemia workup', 'anemia labs', 'low hb workup', 'hematinic profile'],
        items: [REF('cbc', 'CBC'), I('Reticulocyte count'), I('Serum iron'), I('Ferritin'), I('TIBC'), I('Transferrin saturation'), I('Vitamin B12'), I('Folate')],
    },
    {
        id: 'hemolysis-workup', name: 'Hemolysis workup', category: 'Hematology', price: 850,
        aliases: ['hemolysis labs', 'hemolytic anemia workup', 'hemolysis profile', 'ldh haptoglobin retic bilirubin'],
        items: [REF('cbc', 'CBC'), I('Reticulocyte count'), I('LDH'), I('Haptoglobin'), I('Total bilirubin'), I('Direct bilirubin'), I('Peripheral blood smear')],
    },
    // ---------------- Vitamins / Hormones ----------------
    {
        id: 'vitamin-profile', name: 'Vitamin profile', category: 'Nutrition', price: 700,
        aliases: ['vitamin profile', 'vitamin panel', 'nutritional labs', 'nutrition profile'],
        items: [I('Vitamin B12'), I('Folate'), I('Vitamin D')],
    },
    {
        id: 'pregnancy', name: 'Pregnancy test', category: 'Obstetric / Hormone', price: 120,
        aliases: ['pregnancy test', 'upt', 'urine pregnancy test', 'beta hcg', 'bhcg', 'β-hcg', 'serum pregnancy test'],
        items: [I('hCG')],
    },
    // ---------------- Perioperative / blood bank ----------------
    {
        id: 'preop', name: 'Pre-operative labs', category: 'Perioperative', price: 700,
        aliases: ['preop labs', 'pre-op labs', 'preoperative labs', 'pre-op bloods', 'surgery labs'],
        items: [REF('cbc', 'CBC'), I('BUN'), I('Creatinine', 'Cr'), I('Sodium', 'Na'), I('Potassium', 'K'), I('Chloride', 'Cl'), I('Bicarbonate (CO₂/HCO₃)', 'CO2'), I('PT'), I('INR'), I('aPTT')],
    },
    {
        id: 'blood-bank', name: 'Blood bank set', category: 'Blood bank', price: 400,
        aliases: ['blood bank', 'group match', 'gxm', 'type and screen', 'type screen', 'abo rh', 'crossmatch'],
        items: [I('ABO group'), I('Rh type'), I('Antibody screen')],
    },
    // ---------------- Infectious disease ----------------
    {
        id: 'infectious-serology', name: 'Infectious serology', category: 'Infectious disease', price: 800,
        aliases: ['infectious serology', 'viral serology', 'infection serology', 'screening serology'],
        items: [I('HBsAg'), I('Anti-HBs'), I('Anti-HCV'), I('HIV Ag/Ab')],
    },
    {
        id: 'hepatitis-panel', name: 'Hepatitis panel', category: 'Infectious disease / Liver', price: 1200,
        aliases: ['hepatitis panel', 'viral hepatitis panel', 'hepatitis profile', 'hbv hcv hav'],
        items: [I('HBsAg'), I('Anti-HBs'), I('Anti-HBc IgM'), I('Anti-HBc total'), I('Anti-HCV'), I('HAV IgM')],
    },
    {
        id: 'hiv-monitoring', name: 'HIV monitoring', category: 'Infectious disease', price: 2500,
        aliases: ['hiv monitoring', 'hiv follow up', 'hiv labs', 'art monitoring'],
        items: [I('HIV viral load'), I('CD4 count')],
    },
    // ---------------- GI / Oncology / Immunology ----------------
    {
        id: 'pancreatic-enzymes', name: 'Pancreatic enzymes', category: 'Chemistry / GI', price: 300,
        aliases: ['pancreatic enzymes', 'amylase lipase', 'pancreatitis labs'],
        items: [I('Amylase'), I('Lipase')],
    },
    {
        id: 'tumor-markers', name: 'Tumor markers', category: 'Oncology', price: 2000,
        aliases: ['tumor markers', 'cancer markers', 'oncology markers'],
        items: [I('AFP'), I('CEA'), I('CA 19-9'), I('CA-125'), I('PSA')],
    },
    {
        id: 'autoimmune', name: 'Autoimmune screening', category: 'Immunology', price: 1500,
        aliases: ['autoimmune screen', 'autoimmune screening', 'ana panel', 'rheumatology labs'],
        items: [I('ANA'), I('RF'), I('Anti-CCP'), I('ESR'), I('CRP')],
    },
    {
        id: 'adrenal', name: 'Adrenal function test', category: 'Endocrine', price: 900,
        aliases: ['adrenal function', 'adrenal profile', 'cortisol acth', 'adrenal labs'],
        items: [I('Cortisol'), I('ACTH')],
    },
    {
        id: 'reproductive-hormones', name: 'Reproductive hormone profile', category: 'Endocrine / Reproductive', price: 1800,
        aliases: ['hormone profile', 'female hormone profile', 'male hormone profile', 'reproductive hormones', 'fertility hormones'],
        items: [I('LH'), I('FSH'), I('Estradiol'), I('Progesterone'), I('Prolactin'), I('Testosterone')],
    },
    // ---------------- Coagulation / Emergency workups ----------------
    {
        id: 'dic', name: 'DIC profile', category: 'Hematology / Coagulation', price: 900,
        aliases: ['dic profile', 'dic labs', 'disseminated intravascular coagulation labs', 'pt ptt fibrinogen d-dimer platelet'],
        items: [I('Platelet'), I('PT'), I('INR'), I('aPTT'), I('Fibrinogen'), I('D-dimer')],
    },
    {
        id: 'dka', name: 'DKA workup', category: 'Emergency / Endocrine', price: 900,
        aliases: ['dka labs', 'diabetic ketoacidosis labs', 'hyperglycemia workup', 'ketosis labs', 'dka workup'],
        items: [I('Glucose'), I('Sodium', 'Na'), I('Potassium', 'K'), I('Chloride', 'Cl'), I('Bicarbonate (CO₂/HCO₃)', 'CO2'), I('BUN'), I('Creatinine', 'Cr'), I('Serum ketone'), I('Urine ketone'), REF('vbg', 'VBG')],
    },
];
/* ------------------------------------------------------------------ *
 *  Normalization + matching helpers
 * ------------------------------------------------------------------ */
/** Lowercase, trim, collapse whitespace, normalize separators/symbols. */
export function normalizeLabInput(input) {
    return String(input || '')
        .toLowerCase()
        .replace(/[._/\\]+/g, ' ') // separators → space
        .replace(/&/g, ' and ')
        .replace(/[()]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/** Common misspellings / shorthand → canonical token (normalized form). */
const TYPO_MAP = {
    'nutrophil': 'neutrophil',
    'neutrophfil': 'neutrophil',
    'eosi': 'eosinophil',
    'lym': 'lymphocyte',
    'cr': 'creatinine',
    'bun cr': 'renal function test',
    'co3': 'bicarbonate',
    'hco3': 'bicarbonate',
    'elyte': 'electrolytes',
    'e lyte': 'electrolytes',
    'renal feunction test': 'renal function test',
    'thyroid fuction test': 'thyroid function test',
    'thyroid funtion test': 'thyroid function test',
    'tft': 'thyroid function test',
    'rft': 'renal function test',
    'lft': 'liver function test',
    'cbc diff': 'cbc',
};
/** All unique child items across every set (the "single lab" pool). */
export function allSingleLabs() {
    const map = new Map();
    for (const s of LAB_SETS) {
        for (const it of s.items) {
            if (LAB_SETS.some((x) => x.id === it.code))
                continue; // skip set-refs
            const key = it.code.toLowerCase();
            if (!map.has(key))
                map.set(key, it);
        }
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
}
/** Distinct category names, in registry order. */
export function labSetCategories() {
    const seen = new Set();
    const out = [];
    for (const s of LAB_SETS)
        if (!seen.has(s.category)) {
            seen.add(s.category);
            out.push(s.category);
        }
    return out;
}
/** Recursively expand a set into leaf items, de-duplicated by code. */
export function expandLabSet(id, seen = new Set()) {
    const set = LAB_SETS.find((s) => s.id === id);
    if (!set || seen.has(id))
        return [];
    seen.add(id);
    const out = [];
    for (const it of set.items) {
        const childSet = LAB_SETS.find((s) => s.id === it.code);
        if (childSet)
            out.push(...expandLabSet(childSet.id, seen));
        else
            out.push(it);
    }
    return dedupeLabItems(out);
}
/** De-duplicate items by lowercased code, keeping first occurrence. */
export function dedupeLabItems(items) {
    const map = new Map();
    for (const it of items) {
        const k = it.code.toLowerCase();
        if (!map.has(k))
            map.set(k, it);
    }
    return [...map.values()];
}
/** Resolve a free-text input to a set via name / alias / typo map. */
export function findMatchingLabSet(input) {
    const norm = normalizeLabInput(input);
    if (!norm)
        return undefined;
    const canon = TYPO_MAP[norm] || norm;
    return LAB_SETS.find((s) => normalizeLabInput(s.name) === canon ||
        s.id === canon ||
        s.aliases.some((a) => normalizeLabInput(a) === canon));
}
/**
 * Search both panels and single labs. Matches name / id / alias / typo, with a
 * substring fallback so partial typing ("renal", "elyte", "tsh") still resolves.
 * Sets are returned before singles. Limited to `limit` results.
 */
export function searchLabRegistry(query, limit = 12) {
    const norm = normalizeLabInput(query);
    if (!norm)
        return [];
    const canon = TYPO_MAP[norm] || norm;
    const setHits = [];
    for (const s of LAB_SETS) {
        const hay = [s.name, s.id, ...s.aliases].map(normalizeLabInput);
        const hit = hay.some((h) => h === canon || h.includes(canon) || canon.includes(h));
        if (hit)
            setHits.push({ type: 'set', id: s.id, name: s.name, category: s.category, items: expandLabSet(s.id) });
    }
    const singleHits = [];
    for (const it of allSingleLabs()) {
        const hay = [normalizeLabInput(it.name), normalizeLabInput(it.code)];
        if (hay.some((h) => h === canon || h.includes(canon) || canon.includes(h))) {
            singleHits.push({ type: 'single', code: it.code, name: it.name });
        }
    }
    return [...setHits, ...singleHits].slice(0, limit);
}
//# sourceMappingURL=labSets.js.map